import { useState, useRef, useCallback, useMemo, useId, useEffect } from 'react';
import { useDebounce } from '@/hooks/ui/useDebounce';


export interface SelectorOption {
  value: string;
  label: string;
  disabled?: boolean;
  supportingText?: string;
  searchTokens?: string;
}

export interface UseSelectorOptions {
  id?: string;
  value?: string | string[];
  options: SelectorOption[];
  multiple?: boolean;
  disabled?: boolean;
  onChange?: (value: string | string[]) => void;
  /** Callback for server-side search (disables client-side filtering when provided) */
  onSearchChange?: (search: string) => void;
}

export interface UseSelectorReturn {
  baseId: string;
  isMultiple: boolean;
  isControlled: boolean;
  open: boolean;
  search: string;
  activeIndex: number;
  isFocused: boolean;
  triggerWidth: number | null;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  listRef: React.RefObject<HTMLUListElement | null>;
  currentValue: string | undefined;
  selectedValues: string[];
  filtered: SelectorOption[];
  focusSelectedIndex: number;
  /** Whether server-side search is enabled */
  isServerSideSearch: boolean;
  setSearch: (value: string) => void;
  setActiveIndex: (index: number) => void;
  setIsFocused: (focused: boolean) => void;
  toggleValue: (value: string) => void;
  openMenu: () => void;
  closeMenu: () => void;
}

export function useSelector({
  id,
  value,
  options,
  multiple = false,
  disabled = false,
  onChange,
  onSearchChange,
}: UseSelectorOptions): UseSelectorReturn {
  const generatedId = useId();
  const baseId = id || generatedId;
  const isMultiple = !!multiple;
  const isControlled = isMultiple ? Array.isArray(value) : typeof value === 'string';
  const isServerSideSearch = !!onSearchChange;
  // Used to skip the onSearchChange call on initial mount (consumer handles its own initial load)
  const hasMountedRef = useRef(false);

  const [internalValue, setInternalValue] = useState<string | undefined>(undefined);
  const [internalValues, setInternalValues] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const [triggerWidth, setTriggerWidth] = useState<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  // Debounced search value for server-side search (uses default 500ms from useDebounce)
  const debouncedSearch = useDebounce(search);

  // Once a single-select consumer hands us a real string value, treat it as controlled
  // for the rest of its lifetime. Without this latch, a parent clearing its state back to
  // `undefined` (e.g. a dependent field reset) makes `isControlled` flip to false and
  // `currentValue` silently falls back to the stale `internalValue` from the last selection
  // instead of reflecting the cleared value.
  const hasBeenControlledRef = useRef(false);
  if (!isMultiple && typeof value === 'string') {
    hasBeenControlledRef.current = true;
  }
  const isSingleValueControlled = hasBeenControlledRef.current;

  const currentValue = !isMultiple
    ? (isSingleValueControlled ? (value as string | undefined) : internalValue)
    : undefined;

  const selectedValues = useMemo<string[]>(() => {
    if (isMultiple) {
      if (isControlled) {
        return ((value as string[]) || []);
      }
      return internalValues;
    }
    return currentValue !== undefined ? [currentValue] : [];
  }, [isMultiple, isControlled, value, internalValues, currentValue]);

  // Client-side filtering only when server-side search is NOT enabled
  const filtered = useMemo(() => {
    // Step 1: Apply filtering (client-side or pass-through for server-side)
    let result: SelectorOption[];
    if (isServerSideSearch) {
      result = options;
    } else if (!search) {
      result = options;
    } else {
      const query = search.toLowerCase();
      result = options.filter((option) => {
        const haystack = `${option.label}\n${option.supportingText ?? ''}\n${option.searchTokens ?? ''}`.toLowerCase();
        return haystack.includes(query);
      });
    }

    // Step 2: In multi-select mode, bubble selected options to the top while
    // preserving the relative order within each group (selected vs unselected).
    // This runs after filtering so search/filter behavior is fully respected.
    // Single-pass partition to avoid traversing result twice.
    if (!isMultiple || selectedValues.length === 0) return result;

    const selectedSet = new Set(selectedValues);
    const selectedOptions: SelectorOption[] = [];
    const unselectedOptions: SelectorOption[] = [];
    for (const option of result) {
      if (selectedSet.has(option.value)) {
        selectedOptions.push(option);
      } else {
        unselectedOptions.push(option);
      }
    }
    return [...selectedOptions, ...unselectedOptions];
  }, [options, search, isServerSideSearch, isMultiple, selectedValues]);

  const focusSelectedIndex = filtered.findIndex((option) => selectedValues.includes(option.value));

  // Notify server-side search when debounced value changes.
  // Skip the initial mount: the consumer (e.g. useEmployeeSelector) already handles
  // the first load internally. Firing onSearchChange('') here creates a second
  // concurrent request that can race and overwrite filtered results with stale data.
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (isServerSideSearch && onSearchChange) {
      onSearchChange(debouncedSearch);
    }
  }, [debouncedSearch, isServerSideSearch, onSearchChange]);

  const toggleValue = useCallback((val: string) => {
    if (isMultiple) {
      const exists = selectedValues.includes(val);
      const next = exists
        ? selectedValues.filter((selectedValue) => selectedValue !== val)
        : [...selectedValues, val];
      if (!isControlled) {
        setInternalValues(next);
      }
      onChange?.(next);
    } else {
      if (!isSingleValueControlled) setInternalValue(val);
      onChange?.(val);
      setOpen(false);
      setSearch('');
      triggerRef.current?.focus();
    }
  }, [isMultiple, selectedValues, isControlled, isSingleValueControlled, onChange]);

  const openMenu = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    setIsFocused(true);
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setTriggerWidth(rect.width);
    }
  }, [disabled]);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
    setIsFocused(false);
    // Reset search when closing - this triggers onSearchChange with empty string
    // which resets the server-side results to page 1
    setSearch('');
  }, []);

  return {
    baseId,
    isMultiple,
    isControlled,
    open,
    search,
    activeIndex,
    isFocused,
    triggerWidth,
    triggerRef,
    listRef,
    currentValue,
    selectedValues,
    filtered,
    focusSelectedIndex,
    isServerSideSearch,
    setSearch,
    setActiveIndex,
    setIsFocused,
    toggleValue,
    openMenu,
    closeMenu,
  };
}
